var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Slice } from 'prosemirror-model';
import { AllSelection, TextSelection } from 'prosemirror-state';
import { RemoveMarkStep } from 'prosemirror-transform';
import { doLiftNode } from '../basic/keymap/behavior';
import { removeMark } from '../libs';
var clearFormat = function (view) {
    var tr = view.state.tr;
    var _a = view.state, selection = _a.selection, doc = _a.doc;
    selection.ranges.forEach(function (_a) {
        var $from = _a.$from, $to = _a.$to;
        var curMark = (tr.storedMarks || []).concat($from.marks());
        curMark.forEach(function (mark) { return tr.removeStoredMark(mark); });
        removeMark(doc, tr, $from.pos, $to.pos);
    });
    view.dispatch(tr);
    view.focus();
    return true;
};
var getMarkState = function (mark) {
    if (Object.keys(mark.attrs).length) {
        return mark.attrs;
    }
    else {
        return true;
    }
};
var getFormat = function (view, range) {
    var _a = view.state, doc = _a.tr.doc, selection = _a.selection, storedMarks = _a.storedMarks;
    var docName = doc.type.name;
    var paragraphName = doc.type.contentMatch.defaultType.name;
    var from = selection.from, to = selection.to, $from = selection.$from;
    if (range && typeof range.index === 'number' && typeof range.length === 'number') {
        from = range.index;
        to = from + range.length;
        $from = doc.resolve(from);
    }
    var res = {};
    var parentNode = $from.depth === 1 ? $from.parent : $from.node(-1);
    if (!parentNode)
        parentNode = view.state.doc;
    if (![docName, paragraphName].includes(parentNode.type.name)) {
        res[parentNode.type.name] = parentNode.attrs;
    }
    if (from !== to) {
        doc.nodesBetween(from, to, function (node, pos, parent) {
            node.marks.forEach(function (mark) {
                res[mark.type.name] = getMarkState(mark);
            });
            if (node.isTextblock) {
                // maybe nested node
                if (parent.type.name !== docName) {
                    res[parent.type.name] = parent.attrs;
                }
                else if (node.type.name !== paragraphName) {
                    res[node.type.name] = node.attrs;
                }
            }
            return !node.isLeaf;
        });
    }
    else {
        var curMarks = storedMarks ? storedMarks : $from.marks();
        if (curMarks.length) {
            curMarks.forEach(function (mark) {
                res[mark.type.name] = getMarkState(mark);
            });
        }
    }
    return res;
};
// handler for mark's spec `excludes`，remove all the marks that can't coexist. (the default behavior of `prosemirror` is prevent)
var removeConflictMark = function (tr, markType) {
    var res = tr;
    tr.selection.ranges.forEach(function (_a) {
        var $from = _a.$from, $to = _a.$to;
        tr.doc.nodesBetween($from.pos, $to.pos, function (node, nodePos) {
            if (node.isText && node.marks.length) {
                node.marks.forEach(function (mark) {
                    var excludes = mark.type.spec.excludes || '';
                    if (excludes === '_' || excludes.includes(markType.name)) {
                        res = res.step(new RemoveMarkStep(Math.max($from.pos, nodePos), Math.min($to.pos, nodePos + node.nodeSize), mark));
                    }
                });
            }
        });
    });
    return res;
};
var replaceMark = function (markType, attrs, nTr) {
    var tr = removeConflictMark(nTr, markType);
    nTr.selection.ranges.forEach(function (_a) {
        var $from = _a.$from, $to = _a.$to;
        if ($from.pos === $to.pos) {
            tr.removeStoredMark(markType).addStoredMark(markType.create(attrs));
        }
        else {
            tr.removeMark($from.pos, $to.pos, markType).addMark($from.pos, $to.pos, markType.create(attrs));
        }
    });
    return nTr;
};
var markApplies = function (doc, ranges, type) {
    var loop = function (i) {
        var ref = ranges[i];
        var $from = ref.$from;
        var $to = ref.$to;
        var can = $from.depth === 0 ? doc.type.allowsMarkType(type) : false;
        doc.nodesBetween($from.pos, $to.pos, function (node) {
            if (can) {
                return false;
            }
            can = node.inlineContent && node.type.allowsMarkType(type);
        });
        if (can) {
            return { v: true };
        }
    };
    for (var i = 0; i < ranges.length; i++) {
        var returned = loop(i);
        if (returned)
            return returned.v;
    }
    return false;
};
var toggleMark = function (markType, attrs, nTr, storedMarks) {
    var _a = nTr.selection, empty = _a.empty, $cursor = _a.$cursor, ranges = _a.ranges;
    if ((empty && !$cursor) || !markApplies(nTr.doc, ranges, markType)) {
        return nTr;
    }
    var tr = attrs ? removeConflictMark(nTr, markType) : nTr;
    if ($cursor) {
        if (attrs) {
            tr = tr.addStoredMark(markType.create());
        }
        else if (markType.isInSet(storedMarks || $cursor.marks())) {
            tr = tr.removeStoredMark(markType);
        }
    }
    else {
        var has = false;
        for (var i = 0; !has && i < ranges.length; i++) {
            var ref$1 = ranges[i];
            var $from = ref$1.$from;
            var $to = ref$1.$to;
            has = nTr.doc.rangeHasMark($from.pos, $to.pos, markType);
        }
        for (var i$1 = 0; i$1 < ranges.length; i$1++) {
            var ref$2 = ranges[i$1];
            var $from$1 = ref$2.$from;
            var $to$1 = ref$2.$to;
            if (attrs) {
                tr.addMark($from$1.pos, $to$1.pos, markType.create());
            }
            else if (has) {
                tr.removeMark($from$1.pos, $to$1.pos, markType);
            }
        }
    }
    return tr;
};
// check whether there are `isolating` nodes in the range
var containsIsolatingNode = function (ranges) {
    var $from = ranges[0].$from;
    var $to = ranges[0].$to;
    var isContain = false;
    $from.doc.nodesBetween($from.pos, $to.pos, function (node, pos) {
        if (isContain)
            return true;
        if (node.type.spec.isolating) {
            // `from` and `to` should not contain by same `isolating` node or selected the isolating node
            if ($from.pos <= pos || $to.pos >= pos + node.nodeSize) {
                isContain = true;
            }
        }
    });
    return isContain;
};
// return an array containing the contents of each `range` according to `selection.ranges`
var getSelectedInfos = function (_a) {
    var doc = _a.doc, selection = _a.selection;
    var selectedInfos = [];
    var ranges = selection.ranges.sort(function (a, b) { return a.$from.pos - b.$from.pos; });
    // the location to start collecting, mainly used for isolating nodes
    var startPos = selection.$from.depth ? ranges[0].$from.before(1) : 0;
    var containIsolatingNode = ranges.length === 1 && containsIsolatingNode(ranges);
    ranges.forEach(function (_a) {
        var $to = _a.$to, $selectFrom = _a.$from;
        var $from = $selectFrom;
        var extendedFrom = $from.pos;
        var extendedTo = $to.pos;
        var from = $from.pos;
        var to = $to.pos;
        var selectedNodes = [];
        doc.nodesBetween(from, to, function (curNode, nodePos, parent) {
            if (nodePos < startPos)
                return false;
            // do not process cross-area selected isolating nodes
            if (containIsolatingNode && curNode.type.spec.isolating) {
                startPos = nodePos + curNode.nodeSize;
                $from &&
                    selectedNodes.length &&
                    selectedInfos.push({
                        selectedNodes: selectedNodes,
                        selectedRange: {
                            $from: $from,
                            $to: doc.resolve(nodePos),
                        },
                        wrappingRange: {
                            from: extendedFrom,
                            to: nodePos,
                        },
                    });
                selectedNodes = [];
                $from = null;
                extendedFrom = startPos;
                return false;
            }
            // collect the `inline block` and `leaf` node
            if (curNode.inlineContent) {
                var $pos = doc.resolve(nodePos + 1);
                if (!$from)
                    $from = $pos;
                var blockRange = $pos.blockRange();
                if (!blockRange)
                    blockRange = { start: nodePos, end: nodePos };
                if (!selectedNodes.some(function (_a) {
                    var node = _a.node;
                    return node === curNode;
                })) {
                    selectedNodes.push({
                        node: curNode,
                        nodeRange: blockRange,
                    });
                }
                var fixFrom = extendedFrom;
                var fixTo = extendedTo;
                // fix the problem of incorrect range selection when selecting nodes, and stop when meet an `isolating` parent node
                if ($pos.depth > 1 && !parent.type.spec.isolating) {
                    if (parent.firstChild === curNode) {
                        fixFrom = $pos.before(-1);
                    }
                    if (parent.lastChild === curNode) {
                        fixTo = $pos.after(-1);
                    }
                }
                extendedFrom = Math.min(extendedFrom, blockRange.start, fixFrom);
                extendedTo = Math.max(extendedTo, blockRange.end, fixTo);
                return false;
            }
            else if (curNode.isLeaf) {
                selectedNodes.push({
                    node: curNode,
                    nodeRange: { start: nodePos, end: nodePos },
                });
            }
            return true;
        });
        selectedNodes.length &&
            selectedInfos.push({
                selectedNodes: selectedNodes,
                selectedRange: {
                    $from: $from,
                    $to: $to,
                },
                wrappingRange: {
                    from: extendedFrom,
                    to: extendedTo,
                },
            });
    });
    return selectedInfos.sort(function (a, b) { return b.selectedRange.$from.pos - a.selectedRange.$from.pos; });
};
// fix the incorrect pos in selectedNodes after the list level is changed
var fixNodeRange = function (tr, wrappingRange, nodes) {
    var doc = tr.doc;
    var from = wrappingRange.from, to = wrappingRange.to;
    var i = 0;
    doc.nodesBetween(from, to > doc.nodeSize - 2 ? doc.nodeSize - 2 : to, function (node, pos) {
        if (nodes[i] && node === nodes[i].node) {
            nodes[i++].nodeRange = doc.resolve(pos + 1).blockRange();
            return false;
        }
        if (i >= nodes.length)
            return false;
        return !node.isLeaf;
    });
    return nodes;
};
// lift all nested nodes to the top level
var liftNestedNode = function (selectedNodes, _tr, wrappingRange) {
    var shouldFixRange = false;
    var extendedFrom = wrappingRange.from, extendedTo = wrappingRange.to;
    var tr = _tr;
    var _a = tr.selection, from = _a.from, to = _a.to;
    var res = [];
    for (var i = selectedNodes.length - 1; i >= 0; i--) {
        var _b = selectedNodes[i], node = _b.node, nodeRange = _b.nodeRange;
        var topPos = _tr.doc.resolve(nodeRange.start).before(1);
        // stop depth calculation until `isolating` node or root
        var originDepth = nodeRange.depth;
        for (var j = nodeRange.depth; j >= 0; j--) {
            var parent_1 = nodeRange.$from.node(j);
            originDepth = nodeRange.depth - j;
            if (parent_1.type.spec.isolating) {
                break;
            }
        }
        var depth = originDepth;
        var initPos = nodeRange.start + 1;
        var pos = initPos;
        while (depth > 0) {
            tr = doLiftNode(tr, pos);
            pos = tr.mapping.map(initPos);
            depth--;
            shouldFixRange = true;
        }
        res.unshift({ node: node, nodeRange: nodeRange, originDepth: originDepth, topPos: topPos });
    }
    if (shouldFixRange)
        res = fixNodeRange(tr, wrappingRange, res);
    extendedFrom = tr.mapping.map(extendedFrom);
    extendedTo = tr.mapping.map(extendedTo);
    from = tr.mapping.map(from);
    to = tr.mapping.map(to);
    return {
        nodes: res,
        extendedFrom: extendedFrom,
        extendedTo: extendedTo,
        from: from,
        to: to,
        docSize: tr.doc.nodeSize,
    };
};
var checkAndMergeNestedItems = function (nestedItems, nodeType) {
    var start = null;
    var end = null;
    var _loop_1 = function (i) {
        if (nestedItems[i]) {
            if (start === null) {
                start = i;
                end = i;
            }
            else if (typeof end === 'number') {
                end++;
            }
            // combine or reMarkup or keep all previous child nodes into one node
        }
        else if (typeof start === 'number' && typeof end === 'number') {
            var canNestedSelf = nodeType.contentMatch.matchType(nodeType);
            var defaultContentType_1 = nodeType.contentMatch.defaultType;
            var children = nestedItems.slice(start, end + 1);
            if (defaultContentType_1) {
                // if it can not nest itself, just fallback to the default content type
                if (!canNestedSelf) {
                    children = children.map(function (node) { return defaultContentType_1.create(node.attrs, node.content, node.marks); });
                }
                else {
                    children = children.map(function (node) {
                        if (!nodeType.contentMatch.matchType(node.type)) {
                            return defaultContentType_1.create(node.attrs, node.content, node.marks);
                        }
                        return node;
                    });
                }
            }
            var newItems = canNestedSelf ? [nodeType.createAndFill({}, children)] : children;
            nestedItems.splice.apply(nestedItems, __spreadArray(__spreadArray([start, children.length], newItems, false), new Array(children.length - newItems.length), false));
            i = start + newItems.length;
            start = null;
            end = null;
        }
        out_i_1 = i;
    };
    var out_i_1;
    for (var i = 0; i <= nestedItems.length + 1; i++) {
        _loop_1(i);
        i = out_i_1;
    }
    return nestedItems;
};
// generate nested nodes based on nesting information
var createNestedNode = function (nodes, nodeType) {
    var sortedNestItems = nodes.reduce(function (res, selectedNode, idx) {
        var depth = selectedNode.originDepth - 1;
        var result = { node: selectedNode.node, idx: idx };
        if (!Array.isArray(res[depth])) {
            res[depth] = [result];
        }
        else {
            res[depth].push(result);
        }
        return res;
    }, []);
    var nestedItems = [];
    while (sortedNestItems.length) {
        var curItems = sortedNestItems.pop();
        if (curItems) {
            curItems.forEach(function (_a) {
                var node = _a.node, idx = _a.idx;
                nestedItems[idx] = node;
            });
        }
        if (sortedNestItems.length) {
            nestedItems = checkAndMergeNestedItems(nestedItems, nodeType);
        }
    }
    return nestedItems.filter(function (item) { return item; });
};
/**
 * @param nodeTy node type to be created
 * @param node child node
 * @param attrs attrs of the node to be created
 */
var createNode = function (nodeTy, node, attrs) {
    if (attrs === void 0) { attrs = {}; }
    return nodeTy.createAndFill(Object.assign({}, node.attrs, attrs), node.content, node.marks);
};
// restore selection based on content
var getRangeAfterReplace = function (state, tr, _a) {
    var extendedFrom = _a.extendedFrom, extendedTo = _a.extendedTo, $originFrom = _a.$originFrom, $originTo = _a.$originTo;
    var docSize = tr.doc.nodeSize - 2;
    var newFrom = extendedFrom;
    var newTo = extendedTo;
    if (!(state.selection instanceof AllSelection)) {
        var isMatchFrom_1 = false;
        var defaultPos_1 = null;
        var fromContent_1 = $originFrom.node().textContent;
        var toContent_1 = $originTo.node().textContent;
        tr.doc.nodesBetween(extendedFrom, Math.min(extendedTo, docSize), function (node, pos) {
            if (node.inlineContent) {
                if (defaultPos_1 === null)
                    defaultPos_1 = pos + 1;
                if (!isMatchFrom_1 && node.textContent === fromContent_1) {
                    newFrom = pos + 1 + $originFrom.parentOffset;
                    isMatchFrom_1 = true;
                }
                if (isMatchFrom_1 && node.textContent === toContent_1) {
                    newTo = pos + 1 + Math.min(node.textContent.length, $originTo.parentOffset);
                }
            }
            return isMatchFrom_1 ? false : !node.isLeaf;
        });
        if (!isMatchFrom_1) {
            newFrom = defaultPos_1 || newFrom;
            newTo = newFrom;
        }
    }
    return {
        from: newFrom,
        to: Math.min(newTo, docSize),
    };
};
// apply the format to the node and return to the new selection range
var formatNodeType = function (targetNodeType, attributes, sNodes, selectedRange, wrappingRange, nTr, state) {
    var transaction = nTr;
    // check whether it is a nested node, the text of the nested node should be placed in the defaultType
    var isNestedNodeType = !targetNodeType.isTextblock;
    var contentNodeType = isNestedNodeType ? targetNodeType.contentMatch.defaultType : targetNodeType;
    var _a = liftNestedNode(sNodes, transaction, wrappingRange), selectedNodes = _a.nodes, extendedFrom = _a.extendedFrom, extendedTo = _a.extendedTo, docSize = _a.docSize;
    var newNodes = [];
    for (var i = 0; i < selectedNodes.length; i++) {
        var _b = selectedNodes[i], node = _b.node, originDepth = _b.originDepth;
        if (node.isTextblock && originDepth && isNestedNodeType) {
            // nested textBlock
            var nestedContentNodes = [];
            while (selectedNodes[i] &&
                selectedNodes[i].node.isTextblock &&
                // if same parent node, continue
                (!nestedContentNodes.length ||
                    selectedNodes[i].topPos === nestedContentNodes[nestedContentNodes.length - 1].topPos)) {
                nestedContentNodes.push(selectedNodes[i]);
                i++;
            }
            i--;
            newNodes.push.apply(newNodes, createNestedNode(nestedContentNodes, targetNodeType));
        }
        else {
            var addNode = node;
            if (!addNode.isLeaf && !targetNodeType.contentMatch.matchType(addNode.type)) {
                addNode = createNode(contentNodeType, node, contentNodeType === targetNodeType && attributes);
            }
            addNode && newNodes.push(addNode);
        }
    }
    var rootNodeType = isNestedNodeType ? targetNodeType : state.schema.nodes.doc; // dummy root
    var defaultRootNodeContentType = rootNodeType.contentMatch.defaultType;
    // make sure the content node is accept by rootNode
    if (defaultRootNodeContentType) {
        newNodes.forEach(function (node, idx) {
            if (!rootNodeType.contentMatch.matchType(node.type)) {
                newNodes[idx] = defaultRootNodeContentType.create(node.attrs, node.content, node.marks);
            }
        });
    }
    var root = rootNodeType.createAndFill(attributes, newNodes);
    if (isNestedNodeType) {
        transaction.replaceRangeWith(extendedFrom, extendedTo, root);
    }
    else {
        transaction = transaction.replaceRange(extendedFrom, extendedTo, new Slice(root.content, 0, 0));
    }
    return getRangeAfterReplace(state, transaction, {
        extendedFrom: extendedFrom,
        extendedTo: extendedTo + transaction.doc.nodeSize - docSize,
        $originFrom: selectedRange.$from,
        $originTo: selectedRange.$to,
    });
};
var formatSelection = function (formatType, formatValue, formatRange) { return function (state, tr) {
    var schema = state.schema, doc = state.doc, _a = state.selection, from = _a.from, to = _a.to, ranges = _a.ranges, anchor = _a.anchor, head = _a.head;
    var reSelect = function (_tr) { return _tr; };
    // if `formatRange` is specified, keep the same selected content after setting
    if (formatRange) {
        reSelect = function (_tr) {
            if (state.selection instanceof AllSelection) {
                _tr.setSelection(new AllSelection(_tr.doc));
                // set format before the current selection
            }
            else if (formatRange.to <= from) {
                var diffSize = _tr.doc.nodeSize - state.doc.nodeSize;
                return _tr.setSelection(TextSelection.create(_tr.doc, from + diffSize, to + diffSize));
            }
            else {
                _tr.setSelection(TextSelection.create(_tr.doc, from, to));
            }
            return _tr.setSelection(TextSelection.create(_tr.doc, anchor, head));
        };
        // reset the selection according to the `formatRange`
        tr.setSelection(TextSelection.create(doc, formatRange.from, formatRange.to));
    }
    if (schema.marks[formatType]) {
        if (typeof formatValue === 'boolean') {
            toggleMark(schema.marks[formatType], formatValue, tr, state.storedMarks);
        }
        else {
            replaceMark(schema.marks[formatType], formatValue, tr);
        }
    }
    else if (schema.nodes[formatType]) {
        var nodeType_1 = schema.nodes[formatType];
        var selectedInfos = getSelectedInfos(tr);
        if (!selectedInfos.length)
            return tr;
        var resultFrom_1 = doc.nodeSize;
        var resultTo_1 = 0;
        selectedInfos.forEach(function (_a) {
            var selectedNodes = _a.selectedNodes, wrappingRange = _a.wrappingRange, selectedRange = _a.selectedRange;
            var _b = formatNodeType(formatValue ? nodeType_1 : schema.nodes.paragraph, typeof formatValue === 'boolean' ? undefined : formatValue, selectedNodes, selectedRange, wrappingRange, tr, state), rFrom = _b.from, rTo = _b.to;
            resultFrom_1 = Math.min(resultFrom_1, rFrom);
            resultTo_1 = Math.max(resultTo_1, rTo);
        });
        /**
         * if there are multiple ranges,
         * the position of the node calculated at the beginning needs to consider the change of the previous node size
         */
        if (selectedInfos.length > 1) {
            var diffSize = tr.doc.nodeSize - state.doc.nodeSize;
            resultTo_1 += diffSize - Math.floor(diffSize / selectedInfos.length);
        }
        /**
         * if the selection area is split, or the `format range` actually contains the current `selection`,
         * the selection needs to modified to ensure that the same content is selected
         */
        var firstRange = selectedInfos[0];
        var lastRange = selectedInfos[selectedInfos.length - 1];
        if (formatRange || (firstRange.wrappingRange.from < to && lastRange.wrappingRange.to > from)) {
            reSelect = function (_tr) {
                if (state.selection instanceof AllSelection)
                    return _tr.setSelection(new AllSelection(_tr.doc));
                ranges.sort(function (a, b) { return a.$from.pos - b.$from.pos; });
                var newRange = getRangeAfterReplace(state, _tr, {
                    extendedFrom: Math.min(from, resultFrom_1),
                    extendedTo: Math.max(to, resultTo_1),
                    $originFrom: ranges[0].$from,
                    $originTo: ranges[ranges.length - 1].$to,
                });
                return _tr.setSelection(TextSelection.create(_tr.doc, newRange.from, newRange.to));
            };
        }
        tr.setSelection(TextSelection.create(tr.doc, resultFrom_1, resultTo_1));
    }
    return reSelect(tr);
}; };
export { clearFormat, formatSelection, getFormat };
//# sourceMappingURL=format.js.map